import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import AuthDialog from "./AuthDialog";
import { Bookmark, LogOut, ChefHat, Menu, X } from "lucide-react";

const navItems = [
  { to: "/generate", label: "Generate", testid: "nav-generate" },
  { to: "/library", label: "Library", testid: "nav-library" },
  { to: "/skills", label: "Skills", testid: "nav-skills" },
  { to: "/sauces", label: "Sauces", testid: "nav-sauces" },
  { to: "/ingredients", label: "Larder", testid: "nav-ingredients" },
  { to: "/submit", label: "Submit", testid: "nav-submit" },
];

const Navbar = () => {
  const { user, logout } = useAuth();
  const [authOpen, setAuthOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <>
      <header
        data-testid="site-navbar"
        className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-stone-950/80 border-b border-stone-800"
      >
        <div className="max-w-[1600px] mx-auto px-6 sm:px-10 h-16 flex items-center justify-between">
          <Link
            to="/"
            data-testid="nav-logo"
            className="flex items-center gap-2 group"
          >
            <ChefHat className="w-5 h-5 text-orange-600 group-hover:text-orange-500 transition-colors" strokeWidth={1.5} />
            <span className="font-serif-display text-xl tracking-tight text-stone-100">
              Cinder <span className="italic text-orange-600">&amp;</span> Sorrel
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-10">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={item.testid}
                className={({ isActive }) =>
                  `text-sm tracking-wide uppercase font-medium transition-colors ${
                    isActive ? "text-orange-500" : "text-stone-300 hover:text-stone-100"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-4">
            {user ? (
              <>
                <button
                  data-testid="nav-cookbook"
                  onClick={() => navigate("/cookbook")}
                  className="flex items-center gap-2 text-sm text-stone-300 hover:text-orange-500 transition-colors"
                >
                  <Bookmark className="w-4 h-4" strokeWidth={1.5} />
                  Cookbook
                </button>
                {user.is_admin && (
                  <button
                    data-testid="nav-admin"
                    onClick={() => navigate("/admin")}
                    className="text-xs uppercase tracking-[0.2em] text-orange-500 hover:text-orange-400 transition-colors"
                  >
                    Admin
                  </button>
                )}
                <button
                  data-testid="nav-subscribe"
                  onClick={() => {
                    const paid = user.is_admin || ["active", "canceled"].includes(user.subscription_status);
                    navigate(paid ? "/account" : "/subscribe");
                  }}
                  className="text-xs uppercase tracking-[0.2em] text-lime-500 hover:text-lime-400 transition-colors"
                >
                  {user.is_admin || ["active", "canceled"].includes(user.subscription_status) ? "Account" : "Kitchen Pass"}
                </button>
                <div className="w-px h-6 bg-stone-800" />
                <span className="text-xs text-stone-500 font-mono-accent">
                  {user.display_name}
                </span>
                <button
                  data-testid="nav-logout"
                  onClick={logout}
                  className="text-stone-400 hover:text-orange-500 transition-colors"
                  aria-label="Log out"
                >
                  <LogOut className="w-4 h-4" strokeWidth={1.5} />
                </button>
              </>
            ) : (
              <button
                data-testid="nav-signin"
                onClick={() => setAuthOpen(true)}
                className="px-5 py-2 bg-orange-700 hover:bg-orange-800 text-stone-50 text-xs uppercase tracking-[0.2em] font-medium transition-colors"
              >
                Sign in
              </button>
            )}
          </div>

          <button
            className="md:hidden text-stone-200"
            data-testid="nav-mobile-toggle"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>

        {mobileOpen && (
          <div className="md:hidden border-t border-stone-800 bg-stone-950 px-6 py-6 space-y-4">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={() => setMobileOpen(false)}
                className="block text-sm uppercase tracking-wide text-stone-300 hover:text-orange-500"
              >
                {item.label}
              </NavLink>
            ))}
            {user ? (
              <>
                <button
                  onClick={() => { setMobileOpen(false); navigate("/cookbook"); }}
                  className="block text-sm uppercase tracking-wide text-stone-300"
                >
                  Cookbook
                </button>
                <button
                  onClick={() => {
                    setMobileOpen(false);
                    const paid = user.is_admin || ["active", "canceled"].includes(user.subscription_status);
                    navigate(paid ? "/account" : "/subscribe");
                  }}
                  className="block text-sm uppercase tracking-wide text-lime-500"
                >
                  {user.is_admin || ["active", "canceled"].includes(user.subscription_status) ? "Account" : "Kitchen Pass"}
                </button>
                <button onClick={logout} className="block text-sm uppercase tracking-wide text-stone-300">
                  Log out
                </button>
              </>
            ) : (
              <button
                onClick={() => { setMobileOpen(false); setAuthOpen(true); }}
                className="w-full py-3 bg-orange-700 text-stone-50 text-xs uppercase tracking-[0.2em]"
              >
                Sign in
              </button>
            )}
          </div>
        )}
      </header>

      <AuthDialog open={authOpen} onOpenChange={setAuthOpen} />
    </>
  );
};

export default Navbar;
